import { useState, useRef, useCallback, useEffect, RefObject } from 'react';
import { useSelector } from 'react-redux';

import { encodeToWav, mergeChunks, float32To16BitPCMBuffer } from '../../service/AudioService';
import { useOnnx } from './subHooks/useOnnx';
import { useEventCallback } from '../useEventCallback/useEventCallback';
import { RootState } from '../../redux/store';

const WORKLET_URL = new URL('../../worklets/recorder.worklet.js', import.meta.url);
const SAMPLE_RATE = 16000;

interface AudioRecorderOptions {
    onRecordingEnd?: (blob: Blob) => Promise<void> | void;
    defaultVADActive?: boolean;
    onSpeechStart?: () => void;
    onSpeechEnd?: () => void;
    audioElementRef?: RefObject<HTMLAudioElement>;
}

// --- Default Options ---
const defaultOptions: AudioRecorderOptions = {
    defaultVADActive: false,
    onSpeechStart: undefined,
    onSpeechEnd: undefined,
    audioElementRef: undefined,
};

export const useAudioRecorder = (props: Partial<AudioRecorderOptions>) => {
    const options = { ...defaultOptions, ...props };
    const { selectedMic } = useSelector((state: RootState) => state.appConfigSlice);

    const { defaultVADActive, onSpeechStart, onSpeechEnd, onRecordingEnd, audioElementRef } = options;

    // --- State ---
    const [recording, setRecording] = useState(false);
    const [fullWavBlob, setFullWavBlob] = useState<Blob | null>(null);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // --- Refs ---
    const audioContextRef = useRef<AudioContext | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const micWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
    const audioElementWorkletNodeRef = useRef<AudioWorkletNode | null>(null);
    const fullRecordingWSRef = useRef<WebSocket | null>(null);
    const audioElementChunksRef = useRef<Float32Array[]>([]);
    const recordedChunksRef = useRef<Float32Array[]>([]);
    const isVadActiveRef = useRef(defaultVADActive);
    const isSpeakingRef = useRef(false);
    const { testTaker, insperaDetails } = useSelector((state: RootState) => state.appConfigSlice);

    // --- ONNX Worker ---
    const { workerRef, error: onnxError, ready, initWorker, resetSileroStates } = useOnnx();

    // --- Init & Cleanup ---
    useEffect(() => {
        initWorker();
        return cleanup;
    }, [initWorker]);

    const _onSpeechStart = useEventCallback(onSpeechStart);
    const _onSpeechEnd = useEventCallback(onSpeechEnd);
    const _onRecordingEnd = useEventCallback(onRecordingEnd);

    // --- VAD Controls ---
    const startVAD = useCallback(() => {
        if (recording && !defaultVADActive) isVadActiveRef.current = true;
    }, [recording]);

    const stopVAD = useCallback(() => {
        if (recording && !defaultVADActive) {
            isVadActiveRef.current = false;
            setIsSpeaking(false);
            isSpeakingRef.current = false;
            resetSileroStates();
        }
    }, [recording, resetSileroStates]);

    // --- Start Recording ---
    const startFullRecording = useCallback(async () => {
        if (recording || !ready) return;
        setError(null);

        try {
            // Setup audio context and stream
            audioContextRef.current = new AudioContext({ sampleRate: SAMPLE_RATE });
            mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({
                audio: {
                    deviceId: selectedMic?.deviceId ?? 'default',
                    noiseSuppression: true,
                    echoCancellation: true,
                },
            });

            // register worklet
            await audioContextRef.current.audioWorklet.addModule(WORKLET_URL);

            // Setup microphone worklet nodes
            const micSrc = audioContextRef.current.createMediaStreamSource(mediaStreamRef.current);
            micWorkletNodeRef.current = new AudioWorkletNode(audioContextRef.current, 'recorder-worklet-processor');
            micSrc.connect(micWorkletNodeRef.current);

            // Setup audio element worklet nodes if audioRef is provided. This will record audio from the questions
            if (audioElementRef?.current) {
                const audioElementSrc = audioContextRef.current.createMediaElementSource(audioElementRef.current);
                audioElementWorkletNodeRef.current = new AudioWorkletNode(
                    audioContextRef.current,
                    'recorder-worklet-processor',
                );
                audioElementSrc.connect(audioElementWorkletNodeRef.current);
                audioElementSrc.connect(audioContextRef.current.destination);
                // create websocket for full recording (use same host as other websockets)
                try {
                    // import constants at top-level to construct URL
                    // eslint-disable-next-line @typescript-eslint/no-var-requires
                    const { REACT_APP_WEBSOCKET_URL, apiUrl } = require('../../constants/constants');
                    fullRecordingWSRef.current = new WebSocket(`
                        ${REACT_APP_WEBSOCKET_URL}${apiUrl.FULL_RECORDING}`);
                    fullRecordingWSRef.current.addEventListener('open', () => {
                        console.log('Full recording WebSocket opened');
                        // send metadata
                        const metadata = {
                            testTakerId: testTaker.testTakerId,
                            uniqueSessionId: testTaker.testTakerSessionId,
                            productType: insperaDetails?.product,
                            testStatus: 'in-progress',
                        };
                        fullRecordingWSRef.current?.send(JSON.stringify(metadata));
                    });
                    fullRecordingWSRef.current.addEventListener('close', () =>
                        console.log('Full recording WebSocket closed')
                    );
                    fullRecordingWSRef.current.addEventListener('error', (err) =>
                        console.error('Full recording WebSocket error', err)
                    );
                } catch (err) {
                    console.error('Failed to create full recording websocket', err);
                }

                // Send audio element PCM frames to backend as 16-bit PCM
                audioElementWorkletNodeRef.current.port.onmessage = (e) => {
                    audioElementChunksRef.current.push(new Float32Array(e.data));
                };
            }

            // PCM to worker
            micWorkletNodeRef.current.port.onmessage = (event) => {
                const rawPcm = event.data;

                if (!audioElementRef?.current) {
                    recordedChunksRef.current.push(new Float32Array(rawPcm));
                } else {
                    //merge mic and audio element chunks
                    const micChunk = new Float32Array(rawPcm);
                    const qChunk =
                        audioElementChunksRef.current.length > 0
                            ? audioElementChunksRef.current.shift()!
                            : new Float32Array(micChunk.length);
                    const merged = micChunk.map((v, i) => Math.max(-1, Math.min(1, v + qChunk[i])));
                    recordedChunksRef.current.push(merged);
                    const pcm16Buffer = float32To16BitPCMBuffer(merged); //mergeChunks(recordedChunksRef.current));
                    if (fullRecordingWSRef.current?.readyState === WebSocket.OPEN) {
                        fullRecordingWSRef.current.send(pcm16Buffer);
                    }
                }

                if (workerRef.current) {
                    workerRef.current.postMessage(
                        {
                            type: 'process',
                            data: { pcmData: rawPcm, isVadActive: isVadActiveRef.current },
                        },
                        [rawPcm.buffer],
                    );
                }
            };

            // Worker handler
            if (workerRef.current) {
                workerRef.current.onmessage = (event) => {
                    const { type, data, error } = event.data;

                    if (type === 'silero-processed' && isVadActiveRef.current) {
                        setIsSpeaking((prev) => {
                            if (prev && !data) {
                                _onSpeechEnd?.();
                            }
                            if (!prev && data) {
                                _onSpeechStart?.();
                            }

                            isSpeakingRef.current = Boolean(data);
                            return data;
                        });
                    }
                    if (type === 'error') {
                        console.error('Worker error:', error);
                    }
                };
            }

            setRecording(true);
        } catch (err) {
            if (err instanceof Error) {
                setError(err.message ?? 'An error occurred while setting up the audio recording.');
            } else {
                setError('An unknown error occurred while setting up the audio recording.');
            }
            console.error('Error during recording setup:', err);
        }
    }, [recording, ready, resetSileroStates, workerRef, selectedMic]);

    // --- Stop Recording ---
    const stopFullRecording = useCallback(() => {
        if (!recording) return;
        const merged = mergeChunks(recordedChunksRef.current);
        const blob = encodeToWav(merged);

        setFullWavBlob(blob);
        _onRecordingEnd?.(blob);
        cleanup();
    }, [recording]);

    // --- Cleanup ---
    const cleanup = useCallback(() => {
        mediaStreamRef.current?.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;

        micWorkletNodeRef.current?.disconnect();
        micWorkletNodeRef.current = null;

        audioElementWorkletNodeRef.current?.disconnect();
        audioElementWorkletNodeRef.current = null;

        // close full recording websocket
        try {
            const metadata = {
                testTakerId: testTaker.testTakerId,
                uniqueSessionId: testTaker.testTakerSessionId,
                productType: insperaDetails?.product,
                testStatus: 'completed',
            };
            fullRecordingWSRef.current?.send(JSON.stringify(metadata));
            fullRecordingWSRef.current?.close();
        } catch (err) {
            console.error('Error closing full recording websocket', err);
        }
        fullRecordingWSRef.current = null;

        audioContextRef.current?.close();
        audioContextRef.current = null;

        if (!defaultVADActive) {
            isVadActiveRef.current = false;
        }

        audioElementChunksRef.current = [];
        recordedChunksRef.current = [];

        resetSileroStates();

        setRecording(false);
        setIsSpeaking(false);
        isSpeakingRef.current = false;
    }, [resetSileroStates]);

    return {
        recording,
        startFullRecording,
        stopFullRecording,
        fullWavBlob,
        recordedChunks: recordedChunksRef.current,
        isSpeaking,
        startVAD,
        stopVAD,
        ready,
        error: error || onnxError,
        isSpeakingRef,
    };
};
